/// <reference path="../../nativewind-augmentation.d.ts" />
// Copyright (c) Ping. All rights reserved.
//
// Staged-attachment preview + caption overlay. Opened when the user taps
// a staged tray tile (`AttachmentTray` → `onPressStaged`). Matches the
// `MediaPreviewScreen` viewer design — full-bleed dark scrim, contained
// media, top-right close, prev/next chevrons + a bottom-left thumbnail
// strip when more than one attachment is staged — but with the rich
// caption composer docked at the bottom-center IN PLACE OF the
// download/forward/share toolbar. The host owns the caption draft and
// applies it back onto the ACTIVE staged item via `onConfirm` (navigating
// the carousel reports the active id through `onIndexChange`, so the
// caption always lands on the item currently on screen).
//
// Dismiss model: the dark scrim is a SIBLING behind the content layer
// (not a parent), so clicking inside the composer / on a control never
// bubbles up to dismiss it — the previous parent-Pressable structure made
// the overlay close the instant the caption box was focused on web. Only
// a tap on the empty scrim (which falls through the `box-none` content
// layer) closes.
//
// Cross-platform RN `Modal` + `Pressable`; web renders through a body
// `WebPortal` for instant paint.

import React, { useCallback, useEffect } from 'react';
import { Image, Modal, Pressable, StyleSheet, Text, View, useWindowDimensions } from 'react-native';
import { FileText } from "lucide-react-native";
import { MediaPreviewIcon } from "../MediaPreviewIcon";
import { ChatRoomComposerRich } from "../ChatRoomComposer";
import { WebPortal } from "./WebPortal";

const IS_WEB = typeof document !== "undefined" && typeof window !== "undefined";
const CHROME = '#E8EAED';

export interface AttachmentPreviewOverlayAttachment {
  readonly id: string;
  readonly kind: "image" | "video" | "file";
  readonly previewUri?: string | undefined;
  /** Playable (local object) URL for the `video` kind → inline player. */
  readonly videoUri?: string | undefined;
  readonly name?: string | undefined;
}

export interface AttachmentPreviewOverlayMentionCandidate {
  readonly userId: string;
  readonly displayName: string;
}

export interface AttachmentPreviewOverlayProps {
  readonly open: boolean;
  /**
   * Full staged set, in tray order — drives the carousel. When supplied
   * the overlay pages through all of them; `attachment` is then ignored.
   */
  readonly attachments?: ReadonlyArray<AttachmentPreviewOverlayAttachment> | undefined;
  /** Active index into `attachments`. */
  readonly index?: number | undefined;
  /** Reports a carousel navigation so the host can re-point the active id. */
  readonly onIndexChange?: ((next: number) => void) | undefined;
  /** Legacy single-item entry point (used when `attachments` is omitted). */
  readonly attachment?: AttachmentPreviewOverlayAttachment | undefined;
  /** Confirm with the (rich) caption text — applied to the ACTIVE item. */
  readonly onConfirm: (caption: string) => void;
  readonly onClose: () => void;
  /** @-mention candidates so captions can mention conversation members. */
  readonly mentionCandidates?:
    | ReadonlyArray<AttachmentPreviewOverlayMentionCandidate>
    | undefined;
}

export const AttachmentPreviewOverlay = React.memo<AttachmentPreviewOverlayProps>(
  ({ open, attachments, index, onIndexChange, attachment, onConfirm, onClose, mentionCandidates }) => {
    const { width: winW, height: winH } = useWindowDimensions();

    // Resolve the working set: the carousel list when provided, else the
    // single legacy attachment wrapped as a one-item list.
    const items: ReadonlyArray<AttachmentPreviewOverlayAttachment> =
      attachments !== undefined && attachments.length > 0
        ? attachments
        : attachment !== undefined
          ? [attachment]
          : [];
    const count = items.length;
    const safeIndex = Math.min(Math.max(index ?? 0, 0), Math.max(0, count - 1));
    const active = items[safeIndex];

    const goPrev = useCallback(() => {
      if (count <= 1 || onIndexChange === undefined) return;
      onIndexChange((safeIndex - 1 + count) % count);
    }, [count, safeIndex, onIndexChange]);
    const goNext = useCallback(() => {
      if (count <= 1 || onIndexChange === undefined) return;
      onIndexChange((safeIndex + 1) % count);
    }, [count, safeIndex, onIndexChange]);

    // Keyboard: ←/→ navigate, Esc closes (web only).
    useEffect(() => {
      if (!open || !IS_WEB) return undefined;
      const onKey = (e: KeyboardEvent): void => {
        // Don't hijack arrows while typing the caption.
        const tag = (e.target as { tagName?: string } | null)?.tagName;
        const typing = tag === 'INPUT' || tag === 'TEXTAREA';
        if (e.key === 'Escape') onClose();
        else if (!typing && e.key === 'ArrowLeft') goPrev();
        else if (!typing && e.key === 'ArrowRight') goNext();
      };
      document.addEventListener('keydown', onKey);
      return () => document.removeEventListener('keydown', onKey);
    }, [open, onClose, goPrev, goNext]);

    if (!open || active === undefined) return null;

    const hasPreview = active.previewUri !== undefined && active.previewUri.length > 0;

    const stage = (
      <View style={StyleSheet.absoluteFill}>
        {/* Dark scrim — a SIBLING behind the content, so taps inside the
            composer / on controls never bubble here. Tap the empty scrim
            (falls through the box-none content layer) to dismiss. */}
        <Pressable
          accessibilityRole="none"
          accessibilityLabel="Close preview"
          onPress={onClose}
          style={StyleSheet.absoluteFill}
          className="bg-black/95"
        />

        {/* Content layer — box-none so empty space falls through to the
            scrim, while the media/controls capture their own taps. */}
        <View pointerEvents="box-none" className="flex-1 items-center justify-center">
          {active.kind === "video" && active.videoUri !== undefined && IS_WEB ? (
            // eslint-disable-next-line jsx-a11y/media-has-caption
            <video
              key={active.id}
              src={active.videoUri}
              poster={active.previewUri}
              controls
              autoPlay
              playsInline
              style={{
                maxWidth: winW * 0.9,
                maxHeight: winH * 0.8,
                objectFit: "contain",
                background: "#000",
              }}
            />
          ) : hasPreview ? (
            // Image OR a doc with a rendered page thumbnail (PDF/text).
            <Image
              key={active.id}
              source={{ uri: active.previewUri }}
              resizeMode="contain"
              accessibilityIgnoresInvertColors
              // NativeWind sizing on a react-native-web <Image> collapses
              // to 0×0 — explicit numeric style is the documented fallback
              // (see AttachmentTray). Leave room for the docked composer.
              style={{ width: winW * 0.92, height: winH * 0.8 }}
            />
          ) : (
            <View className="items-center justify-center gap-3 px-6">
              <FileText size={56} strokeWidth={1.5} color={CHROME} />
              <Text
                numberOfLines={1}
                className="font-sans text-[15px] leading-5 text-[#F5F6F8]"
              >
                {active.name ?? "File"}
              </Text>
            </View>
          )}
        </View>

        {/* Prev / next chevrons (carousel of >1). */}
        {count > 1 ? (
          <>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Previous"
              onPress={goPrev}
              className="absolute left-2 top-1/2 w-11 h-11 -mt-[22px] rounded-full items-center justify-center active:bg-white/10"
            >
              <MediaPreviewIcon name="chevron-left" size={24} color={CHROME} accessibilityLabel="Previous" />
            </Pressable>
            <Pressable
              accessibilityRole="button"
              accessibilityLabel="Next"
              onPress={goNext}
              className="absolute right-2 top-1/2 w-11 h-11 -mt-[22px] rounded-full items-center justify-center active:bg-white/10"
            >
              <MediaPreviewIcon name="chevron-right" size={24} color={CHROME} accessibilityLabel="Next" />
            </Pressable>
          </>
        ) : null}

        {/* Top-right close — matches MediaPreviewScreen's chrome. */}
        <View pointerEvents="box-none" className="absolute top-0 right-0 p-5">
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Close"
            onPress={onClose}
            hitSlop={8}
            className="w-11 h-11 rounded-[6px] items-center justify-center active:bg-white/10"
          >
            <MediaPreviewIcon name="close" size={24} color={CHROME} accessibilityLabel="Close" />
          </Pressable>
        </View>

        {/* Bottom-left thumbnail strip (Figma image-group: 60px, 2px gap). */}
        {count > 1 ? (
          <View
            pointerEvents="box-none"
            className="absolute bottom-24 left-6 flex flex-row items-center gap-[2px]"
          >
            {items.map((it, i) => (
              <Pressable
                key={it.id}
                accessibilityRole="button"
                accessibilityLabel={`Attachment ${i + 1}`}
                onPress={() => onIndexChange?.(i)}
                className={`h-[60px] w-[44px] overflow-hidden ${
                  i === safeIndex
                    ? 'rounded-[8px] border-2 border-white'
                    : 'rounded-[6px] opacity-60'
                }`}
              >
                {it.previewUri !== undefined && it.previewUri.length > 0 ? (
                  <Image
                    source={{ uri: it.previewUri }}
                    resizeMode="cover"
                    accessibilityIgnoresInvertColors
                    style={{ width: '100%', height: '100%' }}
                  />
                ) : (
                  <View className="w-full h-full items-center justify-center bg-white/10">
                    <FileText size={20} strokeWidth={1.5} color={CHROME} />
                  </View>
                )}
              </Pressable>
            ))}
          </View>
        ) : null}

        {/* Bottom-center caption composer — same rich composer as the chat
            (formatting + @-mentions). Its send button confirms the caption,
            applied by the host to the ACTIVE staged attachment. Keyed by
            the active id so it resets when paging the carousel. This
            replaces the download/forward/share toolbar. */}
        <View pointerEvents="box-none" className="absolute bottom-0 left-0 right-0 items-center">
          <View className="w-full max-w-[720px] mx-auto px-4 pb-5">
            <ChatRoomComposerRich
              key={active.id}
              onSend={async (text: string) => {
                onConfirm(text);
              }}
              showAttachmentMenu={false}
              showVoiceRecording={false}
              showGifs={false}
              showStickers={false}
              denseComposerPadding
              containerContentClassName="px-sm pt-sm pb-lg"
              {...(mentionCandidates !== undefined ? { mentionCandidates } : {})}
            />
          </View>
        </View>
      </View>
    );

    // Web: render via a body portal so it appears instantly (RN-Web's
    // Modal adds a two-phase mount gate that delays the first paint).
    if (IS_WEB) {
      return (
        <WebPortal>
          <View style={StyleSheet.absoluteFill}>{stage}</View>
        </WebPortal>
      );
    }
    return (
      <Modal
        visible={open}
        transparent
        animationType="none"
        onRequestClose={onClose}
        accessibilityViewIsModal
      >
        {stage}
      </Modal>
    );
  },
);

AttachmentPreviewOverlay.displayName = "AttachmentPreviewOverlay";
